/**
 * What: The one button for a declared page action (#4264). Its loading and disabled state come from the page
 *   lifecycle (`lifecycle.button(action)`), never from the part of the page that renders it, so a remount
 *   cannot reset the spinner. Pages never keep their own `loading` for an action.
 * Transport / data-flow: `onClick` normally calls `lifecycle.run(action, work)`.
 */
import type { ComponentProps, ReactNode } from 'react'
import type { ActionButtonState } from '../page-state'

/** The part of a page's lifecycle helper this button reads; `action` is typed from the page's declared action names. */
export type ActionButtonLifecycle<Action extends string> = { readonly button: (action: Action) => ActionButtonState }

export function ActionButton<Action extends string>({
  slot,
  lifecycle,
  action,
  onClick,
  variant,
  tone,
  disabled = false,
  children,
}: {
  slot?: ComponentProps<'s-button'>['slot']
  lifecycle: ActionButtonLifecycle<Action>
  action: NoInfer<Action>
  onClick: () => unknown
  variant?: ComponentProps<'s-button'>['variant']
  tone?: ComponentProps<'s-button'>['tone']
  /** Extra reason to disable (for example an incomplete form); the page state can only add to it. */
  disabled?: boolean
  children: ReactNode
}) {
  const state = lifecycle.button(action)
  return (
    <s-button
      slot={slot}
      variant={variant}
      tone={tone}
      onClick={onClick}
      disabled={disabled || state.disabled}
      loading={state.loading}
    >
      {children}
    </s-button>
  )
}
